import { MatterCostContextMenu } from "@/components/features/matter/context-menus/MatterCostContextMenu";
import { ServerErrorFallbackCard, Text } from "@/components/ui";
import { CurrencyWrapper } from "@/components/ui/CurrencyWrapper/CurrencyWrapper";
import { Badge } from "@/components/ui/shadcn/Badge/Badge";
import { Button } from "@/components/ui/shadcn/Button/Button";
import { Card } from "@/components/ui/shadcn/Card/Card";
import type { ExpenseViewModel } from "@/hooks/matter-view-models/use-expenses-view-model";
import { useGlobalModal } from "@/hooks/useGlobalModal";
import type { Matter } from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { convertToLocalFromDatabaseDateTime } from "@/lib/utils/dateUtils";
import { formatDate } from "@/lib/utils/format";
import { calculateExpenseTotals } from "@/lib/utils/matters/expense-utils";
import { ErrorBoundary } from "@sentry/react";
import { Pencil } from "lucide-react";
import type { KeyboardEvent } from "react";

interface ExpenseCardProps {
  expense: ExpenseViewModel;
  matter: Matter;
}

const getExpensePaymentStatus = ({
  isPaid,
  isPartiallyPaid,
  totalBalanceRemaining,
}: {
  isPaid: boolean;
  isPartiallyPaid: boolean;
  totalBalanceRemaining: number;
}) => {
  if (isPaid && totalBalanceRemaining === 0) {
    return {
      label: "Paid",
      variant: "success" as const,
    };
  }

  if (isPartiallyPaid) {
    return {
      label: "Partial",
      variant: "warning" as const,
    };
  }

  return {
    label: "Unpaid",
    variant: "destructive" as const,
  };
};

function ExpenseCardDangerous({ expense, matter }: ExpenseCardProps) {
  const { openExpenseModal } = useGlobalModal();

  const formatDateDisplay = (dateString: string) => {
    return formatDate(convertToLocalFromDatabaseDateTime(dateString));
  };

  const isPaid = expense.paidAmount && expense.paidAmount > 0;
  const isPartiallyPaid =
    expense.paidAmount &&
    expense.paidAmount > 0 &&
    expense.cost &&
    expense.paidAmount < expense.cost;
  const hasReduction = Boolean(
    expense.reducedAmount && expense.reducedAmount > 0,
  );
  const { totalBalanceRemaining } = calculateExpenseTotals([expense]);
  const paymentStatus = getExpensePaymentStatus({
    isPaid: Boolean(isPaid),
    isPartiallyPaid: Boolean(isPartiallyPaid),
    totalBalanceRemaining,
  });

  const metaParts = [
    expense.paidByMethod
      ? `Paid by ${expense.paidByMethod}${
          expense.paidByMethodDetails ? ` • ${expense.paidByMethodDetails}` : ""
        }`
      : null,
    expense.dateIncurred
      ? `Incurred ${formatDateDisplay(expense.dateIncurred)}`
      : null,
  ].filter(Boolean);
  const metaText = metaParts.join(" · ");

  const handleEditClick = () => {
    openExpenseModal({
      matterId: expense.matterId,
      expenseId: expense.id,
      editing: true,
    });
  };

  const handleRowKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleEditClick();
    }
  };

  const badges = (
    <div className="flex shrink-0 items-center gap-1.5">
      <Badge
        variant="secondary"
        className="bg-accent text-accent-foreground hover:bg-accent text-xs hover:no-underline"
      >
        {expense.category}
      </Badge>
      {expense.trustChecks.length > 0 && (
        <Badge variant="outline" className="text-xs">
          {expense.trustChecks.length} Trust check
          {expense.trustChecks.length !== 1 ? "s" : ""}
        </Badge>
      )}
      <Badge variant={paymentStatus.variant}>{paymentStatus.label}</Badge>
    </div>
  );

  const amounts = (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 md:flex-nowrap md:whitespace-nowrap">
      <div className="flex items-center gap-1">
        <Text variant="subHeading" as="span">
          Original:
        </Text>
        <Text
          variant="body"
          as="span"
          className={cn(
            "font-semibold",
            hasReduction
              ? "text-muted-foreground line-through"
              : "text-foreground",
          )}
        >
          <CurrencyWrapper value={expense.cost ?? undefined} />
        </Text>
      </div>
      {hasReduction ? (
        <div className="flex items-center gap-1">
          <Text variant="subHeading" as="span">
            Reduced:
          </Text>
          <Text variant="body" as="span" className="font-semibold">
            <CurrencyWrapper value={expense.reducedAmount ?? undefined} />
          </Text>
        </div>
      ) : null}
      {expense.paidAmount != null ? (
        <div className="flex items-center gap-1">
          <Text variant="subHeading" as="span">
            Paid:
          </Text>
          <Text
            variant="body"
            as="span"
            className="font-semibold text-green-600"
          >
            <CurrencyWrapper value={expense.paidAmount} />
          </Text>
        </div>
      ) : null}
      {expense.refunded && expense.refundedAmount ? (
        <div className="flex items-center gap-1">
          <Text variant="subHeading" as="span">
            Refunded:
          </Text>
          <Text variant="body" as="span" className="text-primary font-semibold">
            <CurrencyWrapper value={expense.refundedAmount} />
          </Text>
        </div>
      ) : null}
      <div className="flex items-center gap-1">
        <Text variant="subHeading" as="span">
          Balance:
        </Text>
        <Text
          variant="body"
          as="span"
          className={cn(
            "font-semibold",
            totalBalanceRemaining > 0
              ? "text-destructive"
              : "text-muted-foreground",
          )}
        >
          <CurrencyWrapper value={totalBalanceRemaining} />
        </Text>
      </div>
    </div>
  );

  return (
    <MatterCostContextMenu
      expense={expense}
      matter={matter}
      trigger={
        <Card
          className="border-border hover:bg-muted/50 group w-full cursor-pointer transition-colors"
          data-testid="expense-card"
          role="button"
          tabIndex={0}
          onClick={handleEditClick}
          onKeyDown={handleRowKeyDown}
        >
          <div className="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-center md:gap-4">
            <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
              <Text
                variant="cardTitle"
                className="mb-0 min-w-0 truncate"
                title={expense.label}
              >
                {expense.label}
              </Text>
              {badges}
              {metaParts.length > 0 ? (
                <Text
                  variant="subHeading"
                  className="text-muted-foreground hidden max-w-40 shrink-0 truncate xl:inline xl:max-w-56"
                  title={metaText}
                >
                  {metaText}
                </Text>
              ) : null}
            </div>

            {metaParts.length > 0 ? (
              <Text
                variant="subHeading"
                className="text-muted-foreground min-w-0 md:hidden"
                title={metaText}
              >
                {metaText}
              </Text>
            ) : null}

            <div className="flex shrink-0 items-center gap-2 md:gap-3">
              {amounts}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={(event) => {
                  event.stopPropagation();
                  handleEditClick();
                }}
                className={cn(
                  "text-muted-foreground hover:text-foreground hover:bg-muted/70 focus-visible:bg-muted/70 h-8 w-8 shrink-0",
                  "opacity-100 transition-opacity md:opacity-0 md:group-focus-within:opacity-100 md:group-hover:opacity-100",
                )}
                data-testid="edit-expense-button"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </Card>
      }
    />
  );
}

export const ExpenseCard = (props: ExpenseCardProps) => (
  <ErrorBoundary fallback={<ServerErrorFallbackCard />}>
    <ExpenseCardDangerous {...props} />
  </ErrorBoundary>
);
